import PersistentStore from "@/api/PersistentStore";
import requests from "@/api/request";
import {
  addWindowDimensions,
  getBrowserString,
  postMessage
} from "@/components/shared/util";
import { RootState } from "@/store/types";
import { ActionTree } from "vuex";
import {
  ALERT_REPEAT_INTERVAL_KEY,
  AREA_LIST_KEY,
  AUDIO_ALERTS_ENABLED_KEY,
  PREFERENCES_KEY
} from ".";
import { Preference, ProfileState } from "./types";

export const actions: ActionTree<ProfileState, RootState> = {
  async accessRequest({ commit }, { data, params }): Promise<void> {
    addWindowDimensions(data);
    const result = await requests.accessRequest(data, params);
    commit("accessRequest", result);
    if (result.data != null) {
      if (result.data.areas != null) {
        commit("setAreas", result.data.areas);
        PersistentStore.setItem(AREA_LIST_KEY, result.data.areas);
      }

      if (result.data.isTesting != null) {
        commit("patient/setIsTesting", result.data.isTesting, { root: true });
      }
      if (result.data.isActiveTesting != null && result.data.isActiveTesting) {
        commit(
          "patient/isActiveTest",

          {
            root: true
          }
        );
      }

      if (result.data.isActiveTesting != null) {
        commit("patient/setIsActiveTesting", result.data.isActiveTesting, {
          root: true
        });
      }
    }
  },
  async check({ commit }): Promise<void> {
    const browser = getBrowserString();
    const user = await requests.accessRequest(
      {},
      { action: "check", browser: browser }
    );
    commit("profileLoaded", user);

    if (user.areas != null) {
      commit("setAreas", user.areas);
      PersistentStore.setItem(AREA_LIST_KEY, user.areas);
    }

    commit("patient/isTesting", user.isTesting != null && user.isTesting, {
      root: true
    });
    commit(
      "patient/isPagingTest",
      user.isPagingTest != null && user.isPagingTest,
      {
        root: true
      }
    );
    commit(
      "patient/isPagingTestActive",
      user.isPagingTestActive != null && user.isPagingTestActive,
      {
        root: true
      }
    );
    commit(
      "patient/isActiveTest",
      user.isActiveTesting != null && user.isActiveTesting,
      {
        root: true
      }
    );
  },
  async logout(): Promise<void> {
    await requests.accessRequest({}, { action: "logout" });
    const message = {
      action: "logout"
    };
    postMessage(message);
  },
  async login({ commit }, data): Promise<void> {
    if (process.env.VUE_APP_DEMO) {
      return Promise.resolve();
    }
    const browser = getBrowserString();
    addWindowDimensions(data);
    const user = await requests.accessRequest(data, {
      action: "login",
      browser: browser
    });
    commit("profileLoaded", user);

    if (user.areas != null) {
      commit("setAreas", user.areas);
      PersistentStore.setItem(AREA_LIST_KEY, user.areas);
    }
    commit("patient/isTesting", user.isTesting != null && user.isTesting, {
      root: true
    });
    commit(
      "patient/isPagingTest",
      user.isPagingTest != null && user.isPagingTest,
      {
        root: true
      }
    );
  },
  system({ commit }): void {
    requests.getSystem().then(system => {
      commit("system", system);
      if (system.demo) {
        commit("demo");
      }
    });
  },

  // network test
  async wirelessTestAddNote({ commit }, note: string): Promise<any> {
    return await requests.validateAddNote(note);
  },

  async wirelessTestStart({ commit }, note: string): Promise<any> {
    commit("setIsWirelessTesting", true);
    return await requests.validateStartRequest(note);
  },

  async wirelessTestNote({ commit }, note: string): Promise<any> {
    return await requests.validateAddNote(note);
  },

  async wirelessTestResult({ commit }): Promise<any> {
    return await requests.validateStatus();
  },

  async wirelessTestStop({ commit, state }): Promise<any> {
    commit("setIsWirelessTesting", false);
    return await requests.validateStopRequest();
  },

  // paging test
  async pagingTestStart({ commit }, pagerID: string): Promise<any> {
    commit("setPagerID", pagerID);
    return await requests.validateStartPagingRequest(pagerID);
  },

  async pagingTestNote({ commit }, note: string): Promise<any> {
    return await requests.validateAddPagingNote(note);
  },

  async pagingReceived({ commit }, index: number): Promise<any> {
    return await requests.validateReceivedPage(index);
  },

  async pagingTestResult({ commit }): Promise<any> {
    return await requests.validatePagingResults();
  },

  async pagingTestStop({ commit, state }): Promise<any> {
    return await requests.validateStopPagingRequest();
  },

  async pagingTestComplete({ commit, state }): Promise<any> {
    return await requests.validateCompletePagingRequest();
  },

  async toggleActiveCase({ commit, state }): Promise<any> {
    return await requests.validateToggleActiveRequest();
  },

  async startActiveCase({ commit }, data): Promise<any> {
    const area = data.area;
    const roomSys = data.roomSys;
    return await requests.validateStartActiveRequest(area, roomSys);
  },

  async getActiveCases({ commit, state }): Promise<any[]> {
    const data: any[] = await requests.validateCurrentActiveCasesRequest();
    commit("setActiveCaseTests", data);
    return data;
  },

  setArea({ commit }, area: string) {
    const areaList: Array<string> = PersistentStore.getItem(AREA_LIST_KEY, []);
    // area is the empty string when navigating to product select, don't add
    // that to the area list
    if (area && !areaList.includes(area)) {
      areaList.push(area);
    }
    PersistentStore.setItem(AREA_LIST_KEY, areaList);
    commit("setArea", area);
  },
  setAudioAlertsEnabled({ commit }, enabled: boolean) {
    PersistentStore.setItem(AUDIO_ALERTS_ENABLED_KEY, enabled);
    commit("setAudioAlertsEnabled", enabled);
  },
  setAlertRepeatInterval({ commit }, interval: number) {
    PersistentStore.setItem(ALERT_REPEAT_INTERVAL_KEY, interval);
    commit("setAlertRepeatInterval", interval);
  },
  setSpeechSynthesisEnabled({ commit }, enabled: boolean) {
    commit("setSpeechSynthesisEnabled", enabled);
  },
  async toggleMaskSetting({ commit }) {
    commit("setMaskMode", await requests.toggleMaskSetting());
  },
  setPreference({ commit, state }, data: Preference) {
    commit("setPreference", data);
    PersistentStore.setItem(PREFERENCES_KEY, state.preferences);
  },
  async performanceResults({ commit }, time: string = ""): Promise<any> {
    return await requests.getPerformance(time);
  },

  async commentsRequest({ commit }): Promise<any> {
    return await requests.getComments();
  },

  async commentDataRequest({ commit }, logID: number): Promise<any> {
    return await requests.getCommentData(logID);
  },

  async errorSummary({ commit }): Promise<any> {
    return await requests.errorSummary();
  },

  setShownLoginMs({ commit }, shown: number) {
    commit("setShownLoginMs", shown);
  }
};
